const parseFilenameFromContentDisposition = (contentDisposition) => {
  if (!contentDisposition) {
    return null;
  }

  const filenameMatch = contentDisposition.match(/filename\s*=\s*"([^"]+)"/);
  return filenameMatch?.[1] || null;
};

export const fetchUserRegistrations = async ({
  registrationBaseUrl,
  email,
  conferenceName,
}) => {
  const url = `${registrationBaseUrl}/registration/user/${encodeURIComponent(
    email
  )}/${encodeURIComponent(conferenceName)}`;
  const response = await fetch(url);

  if (!response.ok) {
    return [];
  }

  const registrations = await response.json();
  return Array.isArray(registrations) ? registrations : [];
};

export const fetchPapersByRegistrationId = async ({
  registrationBaseUrl,
  registrationId,
}) => {
  const url = `${registrationBaseUrl}/api/papers/registration/${registrationId}`;
  const response = await fetch(url);

  console.log("🔍 Fetching papers for registration ID:", registrationId);
  console.log("🔍 Paper API URL:", url);
  console.log("🔍 Paper API response status:", response.status);

  if (!response.ok) {
    return [];
  }

  const papers = await response.json();
  console.log("🔍 Papers array from API:", papers);
  console.log("🔍 Number of papers:", papers?.length);

  return Array.isArray(papers) ? papers : [];
};

export const fetchPaperFileMetadata = async ({
  registrationBaseUrl,
  registrationId,
}) => {
  const url = `${registrationBaseUrl}/registration/${registrationId}/file/paper`;
  console.log("🔍 Checking for file at:", url);

  const response = await fetch(url, { method: "HEAD" });
  console.log("🔍 File check response status:", response.status);

  if (!response.ok) {
    console.log("📎 No paper file found, status:", response.status);
    return {
      exists: false,
      fileName: "uploaded-paper.pdf",
      status: response.status,
    };
  }

  const contentDisposition = response.headers.get("Content-Disposition");
  console.log("🔍 Content-Disposition header:", contentDisposition);

  const fileName =
    parseFilenameFromContentDisposition(contentDisposition) || "uploaded-paper.pdf";

  if (fileName === "uploaded-paper.pdf" && contentDisposition) {
    console.log("📎 Could not extract filename from header");
  } else if (fileName !== "uploaded-paper.pdf") {
    console.log("📎 Extracted filename:", fileName);
  }

  console.log("📎 Paper file exists. Final filename:", fileName);

  return {
    exists: true,
    fileName,
    status: response.status,
  };
};

export const fetchCoAuthorsByPaperId = async ({
  registrationBaseUrl,
  paperId,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/api/coauthors/paper/${paperId}`
  );

  if (!response.ok) {
    return [];
  }

  const coAuthors = await response.json();
  return Array.isArray(coAuthors) ? coAuthors : [];
};

export const fetchPaperCount = async ({
  registrationBaseUrl,
  email,
  conference,
}) => {
  const url = `${registrationBaseUrl}/api/papers/count?email=${encodeURIComponent(
    email
  )}&conference=${encodeURIComponent(conference)}`;
  const response = await fetch(url);

  if (!response.ok) {
    return 0;
  }

  const count = await response.json();
  const parsedCount = Number(count);
  return Number.isFinite(parsedCount) ? parsedCount : 0;
};

export const fetchRegistrationFeeForSelection = async ({
  conferenceApi,
  conferenceId,
  categoryId,
  category,
  registrationTypeId,
  registrationType,
}) => {
  const today = new Date().toISOString().split("T")[0];
  const url = `${conferenceApi}/fees/conference/${conferenceId}/valid-on?date=${today}`;
  const response = await fetch(url);

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("No fee configuration found for the current date");
    }
    throw new Error("Failed to fetch registration fee");
  }

  const validFees = await response.json();
  const fees = Array.isArray(validFees) ? validFees : [];

  const normalizeId = (value) =>
    value === null || value === undefined || value === "" ? null : String(value);
  const normalizeText = (value) =>
    value === null || value === undefined
      ? ""
      : String(value).trim().toLowerCase();
  const detailId = (detail, fallback) =>
    normalizeId(detail?.id ?? detail?.detailId ?? fallback);
  const detailValue = (detail, fallback) =>
    normalizeText(detail?.value ?? detail?.name ?? fallback);

  const selectedCategoryId = normalizeId(categoryId);
  const selectedRegistrationTypeId = normalizeId(registrationTypeId);
  const selectedCategoryValue = normalizeText(category);
  const selectedRegistrationTypeValue = normalizeText(registrationType);

  const matchesCategory = (fee) => {
    const feeCategoryId = detailId(
      fee.participantType,
      fee.participantTypeId ?? fee.categoryId
    );
    const feeCategoryValue = detailValue(
      fee.participantType,
      fee.participantTypeValue ?? fee.category
    );

    return (
      (selectedCategoryId && feeCategoryId === selectedCategoryId) ||
      (selectedCategoryValue && feeCategoryValue === selectedCategoryValue)
    );
  };

  const matchesRegistrationType = (fee) => {
    if (!selectedRegistrationTypeId && !selectedRegistrationTypeValue) {
      return false;
    }

    const feeTypeId = detailId(
      fee.eventType,
      fee.eventTypeId ?? fee.registrationTypeId
    );
    const feeTypeValue = detailValue(
      fee.eventType,
      fee.eventTypeValue ?? fee.registrationType
    );

    return (
      (selectedRegistrationTypeId && feeTypeId === selectedRegistrationTypeId) ||
      (selectedRegistrationTypeValue &&
        feeTypeValue === selectedRegistrationTypeValue)
    );
  };

  const isCategoryOnlyFee = (fee) =>
    !fee.eventType &&
    !fee.eventTypeId &&
    !fee.registrationTypeId &&
    !fee.eventTypeValue &&
    !fee.registrationType;

  const activeCategoryFees = fees.filter(
    (fee) => matchesCategory(fee) && fee.isActive === true
  );
  const typeSpecificFee =
    selectedRegistrationTypeId || selectedRegistrationTypeValue
      ? activeCategoryFees.find(matchesRegistrationType)
      : null;
  const categoryOnlyFee = activeCategoryFees.find(isCategoryOnlyFee);
  const matchingFee = typeSpecificFee || categoryOnlyFee || null;

  if (!matchingFee) {
    console.warn("No matching registration fee found", {
      conferenceId,
      selectedCategoryId,
      selectedCategoryValue,
      selectedRegistrationTypeId,
      selectedRegistrationTypeValue,
      validFeeCount: fees.length,
      activeCategoryFeeCount: activeCategoryFees.length,
    });
  }

  return matchingFee;
};

export const fetchAccompanyingPersonFee = async ({
  conferenceApi,
  conferenceId,
}) => {
  const url = `${conferenceApi}/fees/conference/${conferenceId}/AccompanyingFee`;
  const response = await fetch(url);

  if (!response.ok) {
    console.error("❌ Failed to fetch accompanying person fee");
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return null;
  }

  const feeData = await response.json();

  if (Array.isArray(feeData)) {
    return feeData.find((fee) => fee.isActive === true) || null;
  }

  if (feeData && typeof feeData === "object") {
    return feeData.isActive === true || feeData.isActive === undefined
      ? feeData
      : null;
  }

  return null;
};

export const fetchConveyanceFeePercentage = async ({
  conferenceApi,
  conferenceId,
}) => {
  const url = `${conferenceApi}/${conferenceId}/master/type/conveyanceFee/details`;
  const response = await fetch(url);

  if (!response.ok) {
    return null;
  }

  const conveyanceFeeData = await response.json();
  let value = null;

  if (Array.isArray(conveyanceFeeData)) {
    if (conveyanceFeeData.length === 0) {
      return null;
    }
    const activeConveyanceFee =
      conveyanceFeeData.find((fee) => fee.isActive === true) ||
      conveyanceFeeData[0];
    if (activeConveyanceFee) {
      value =
        activeConveyanceFee.value ||
        activeConveyanceFee.percentage ||
        activeConveyanceFee.rate;
    }
  } else if (conveyanceFeeData && typeof conveyanceFeeData === "object") {
    value =
      conveyanceFeeData.value ||
      conveyanceFeeData.percentage ||
      conveyanceFeeData.rate;
  }

  if (value !== null && value !== undefined && value !== "") {
    const parsedValue = parseFloat(value);
    return !Number.isNaN(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  return null;
};

export const fetchFocusedSessions = async ({ conferenceApi, conferenceId }) => {
  const url = `${conferenceApi}/${conferenceId}/master/type/FocussedSessions/details`;
  const response = await fetch(url);

  if (!response.ok) {
    console.error("❌ Failed to fetch focused sessions");
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return [];
  }

  const sessionsData = await response.json();
  return Array.isArray(sessionsData) ? sessionsData : [];
};

export const fetchGenderOptions = async ({ conferenceApi, conferenceId }) => {
  const url = `${conferenceApi}/${conferenceId}/master/type/gender/details`;
  console.log("🔍 Fetching gender options from:", url);

  const response = await fetch(url);

  if (!response.ok) {
    console.error("❌ Failed to fetch gender options");
    console.error("Status:", response.status);
    return [];
  }

  const genderData = await response.json();
  console.log("📦 Gender options received:", genderData);

  return Array.isArray(genderData) ? genderData : [];
};

export const fetchFocusedSessionFee = async ({
  conferenceApi,
  conferenceId,
  sessionId,
}) => {
  const url = `${conferenceApi}/fees/conference/${conferenceId}/fee/${sessionId}`;
  console.log(`🔍 Fetching focused session fee for session ${sessionId} from:`, url);

  const response = await fetch(url);

  if (!response.ok) {
    console.error(`❌ Failed to fetch fee for focused session ${sessionId}`);
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return null;
  }

  const feeData = await response.json();
  console.log(
    `📦 Raw API response for session ${sessionId}:`,
    JSON.stringify(feeData, null, 2)
  );
  console.log(`💰 Fee rate extracted:`, feeData?.rate);
  console.log(`💵 Fee currency extracted:`, feeData?.currency);

  return feeData;
};

export const fetchFocusedSessionAvailability = async ({
  conferenceApi,
  sessionId,
}) => {
  const url = `${conferenceApi}/seats/${sessionId}/availability`;
  console.log(
    `🔍 Fetching focused session availability for session ${sessionId} from:`,
    url
  );

  const response = await fetch(url);

  if (!response.ok) {
    console.error(`❌ Failed to fetch availability for focused session ${sessionId}`);
    console.error("Status:", response.status);
    console.error("Status Text:", response.statusText);
    return null;
  }

  const availabilityData = await response.json();
  console.log(
    `📦 Availability response for session ${sessionId}:`,
    JSON.stringify(availabilityData, null, 2)
  );

  return availabilityData;
};

export const fetchAbstractMetadata = async ({ conferenceApi, conferenceId }) => {
  const response = await fetch(
    `${conferenceApi}/api/abstract-metadata/conference/${conferenceId}`
  );
  const data = await response.json();
  return Array.isArray(data) ? data[0] : data;
};

export const fetchTracks = async ({ conferenceApi, conferenceId }) => {
  const url = `${conferenceApi}/api/tracks/conference/${conferenceId}`;
  console.log("📡 Fetching tracks from:", url);
  const response = await fetch(url);
  console.log("📡 Tracks response status:", response.status);
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

export const fetchConferenceByHost = async ({ conferenceApi, host }) => {
  const response = await fetch(`${conferenceApi}/${host}/json`);
  return await response.json();
};

export const fetchMasterDetails = async ({
  conferenceApi,
  conferenceId,
  type,
}) => {
  const response = await fetch(
    `${conferenceApi}/${conferenceId}/master/type/${type}/details`
  );
  const data = await response.json();
  return Array.isArray(data) ? data : [];
};

// export const fetchCountries = async () => {
//   const response = await fetch("https://restcountries.com/v5/all?fields=name");
//   const data = await response.json();
//   return (Array.isArray(data) ? data : [])
//     .map((country) => {
//       const name =
//         typeof country?.name === "string"
//           ? country.name
//           : country?.name?.common ||
//             country?.name?.official ||
//             country?.name?.nativeName ||
//             null;
//       if (!name) return null;
//       return { label: name, value: name };
//     })
//     .filter(Boolean)
//     .sort((a, b) => a.label.localeCompare(b.label));
// };



export const fetchCountries = async () => {
  return [
    { label: "Afghanistan", value: "Afghanistan" },
    { label: "Albania", value: "Albania" },
    { label: "Algeria", value: "Algeria" },
    { label: "Andorra", value: "Andorra" },
    { label: "Angola", value: "Angola" },
    { label: "Antigua and Barbuda", value: "Antigua and Barbuda" },
    { label: "Argentina", value: "Argentina" },
    { label: "Armenia", value: "Armenia" },
    { label: "Australia", value: "Australia" },
    { label: "Austria", value: "Austria" },
    { label: "Azerbaijan", value: "Azerbaijan" },
    { label: "Bahamas", value: "Bahamas" },
    { label: "Bahrain", value: "Bahrain" },
    { label: "Bangladesh", value: "Bangladesh" },
    { label: "Barbados", value: "Barbados" },
    { label: "Belarus", value: "Belarus" },
    { label: "Belgium", value: "Belgium" },
    { label: "Belize", value: "Belize" },
    { label: "Benin", value: "Benin" },
    { label: "Bhutan", value: "Bhutan" },
    { label: "Bolivia", value: "Bolivia" },
    { label: "Bosnia and Herzegovina", value: "Bosnia and Herzegovina" },
    { label: "Botswana", value: "Botswana" },
    { label: "Brazil", value: "Brazil" },
    { label: "Brunei", value: "Brunei" },
    { label: "Bulgaria", value: "Bulgaria" },
    { label: "Burkina Faso", value: "Burkina Faso" },
    { label: "Burundi", value: "Burundi" },
    { label: "Cambodia", value: "Cambodia" },
    { label: "Cameroon", value: "Cameroon" },
    { label: "Canada", value: "Canada" },
    { label: "Cape Verde", value: "Cape Verde" },
    { label: "Central African Republic", value: "Central African Republic" },
    { label: "Chad", value: "Chad" },
    { label: "Chile", value: "Chile" },
    { label: "China", value: "China" },
    { label: "Colombia", value: "Colombia" },
    { label: "Comoros", value: "Comoros" },
    { label: "Congo", value: "Congo" },
    { label: "Costa Rica", value: "Costa Rica" },
    { label: "Croatia", value: "Croatia" },
    { label: "Cuba", value: "Cuba" },
    { label: "Cyprus", value: "Cyprus" },
    { label: "Czech Republic", value: "Czech Republic" },
    { label: "Denmark", value: "Denmark" },
    { label: "Djibouti", value: "Djibouti" },
    { label: "Dominica", value: "Dominica" },
    { label: "Dominican Republic", value: "Dominican Republic" },
    { label: "Ecuador", value: "Ecuador" },
    { label: "Egypt", value: "Egypt" },
    { label: "El Salvador", value: "El Salvador" },
    { label: "Equatorial Guinea", value: "Equatorial Guinea" },
    { label: "Eritrea", value: "Eritrea" },
    { label: "Estonia", value: "Estonia" },
    { label: "Eswatini", value: "Eswatini" },
    { label: "Ethiopia", value: "Ethiopia" },
    { label: "Fiji", value: "Fiji" },
    { label: "Finland", value: "Finland" },
    { label: "France", value: "France" },
    { label: "Gabon", value: "Gabon" },
    { label: "Gambia", value: "Gambia" },
    { label: "Georgia", value: "Georgia" },
    { label: "Germany", value: "Germany" },
    { label: "Ghana", value: "Ghana" },
    { label: "Greece", value: "Greece" },
    { label: "Grenada", value: "Grenada" },
    { label: "Guatemala", value: "Guatemala" },
    { label: "Guinea", value: "Guinea" },
    { label: "Guyana", value: "Guyana" },
    { label: "Haiti", value: "Haiti" },
    { label: "Honduras", value: "Honduras" },
    { label: "Hungary", value: "Hungary" },
    { label: "Iceland", value: "Iceland" },
    { label: "India", value: "India" },
    { label: "Indonesia", value: "Indonesia" },
    { label: "Iran", value: "Iran" },
    { label: "Iraq", value: "Iraq" },
    { label: "Ireland", value: "Ireland" },
    { label: "Israel", value: "Israel" },
    { label: "Italy", value: "Italy" },
    { label: "Jamaica", value: "Jamaica" },
    { label: "Japan", value: "Japan" },
    { label: "Jordan", value: "Jordan" },
    { label: "Kazakhstan", value: "Kazakhstan" },
    { label: "Kenya", value: "Kenya" },
    { label: "Kuwait", value: "Kuwait" },
    { label: "Kyrgyzstan", value: "Kyrgyzstan" },
    { label: "Laos", value: "Laos" },
    { label: "Latvia", value: "Latvia" },
    { label: "Lebanon", value: "Lebanon" },
    { label: "Lesotho", value: "Lesotho" },
    { label: "Liberia", value: "Liberia" },
    { label: "Libya", value: "Libya" },
    { label: "Lithuania", value: "Lithuania" },
    { label: "Luxembourg", value: "Luxembourg" },
    { label: "Madagascar", value: "Madagascar" },
    { label: "Malawi", value: "Malawi" },
    { label: "Malaysia", value: "Malaysia" },
    { label: "Maldives", value: "Maldives" },
    { label: "Mali", value: "Mali" },
    { label: "Malta", value: "Malta" },
    { label: "Mauritania", value: "Mauritania" },
    { label: "Mauritius", value: "Mauritius" },
    { label: "Mexico", value: "Mexico" },
    { label: "Moldova", value: "Moldova" },
    { label: "Monaco", value: "Monaco" },
    { label: "Mongolia", value: "Mongolia" },
    { label: "Montenegro", value: "Montenegro" },
    { label: "Morocco", value: "Morocco" },
    { label: "Mozambique", value: "Mozambique" },
    { label: "Myanmar", value: "Myanmar" },
    { label: "Namibia", value: "Namibia" },
    { label: "Nepal", value: "Nepal" },
    { label: "Netherlands", value: "Netherlands" },
    { label: "New Zealand", value: "New Zealand" },
    { label: "Nigeria", value: "Nigeria" },
    { label: "Norway", value: "Norway" },
    { label: "Oman", value: "Oman" },
    { label: "Pakistan", value: "Pakistan" },
    { label: "Panama", value: "Panama" },
    { label: "Paraguay", value: "Paraguay" },
    { label: "Peru", value: "Peru" },
    { label: "Philippines", value: "Philippines" },
    { label: "Poland", value: "Poland" },
    { label: "Portugal", value: "Portugal" },
    { label: "Qatar", value: "Qatar" },
    { label: "Romania", value: "Romania" },
    { label: "Russia", value: "Russia" },
    { label: "Saudi Arabia", value: "Saudi Arabia" },
    { label: "Singapore", value: "Singapore" },
    { label: "South Africa", value: "South Africa" },
    { label: "South Korea", value: "South Korea" },
    { label: "Spain", value: "Spain" },
    { label: "Sri Lanka", value: "Sri Lanka" },
    { label: "Sweden", value: "Sweden" },
    { label: "Switzerland", value: "Switzerland" },
    { label: "Thailand", value: "Thailand" },
    { label: "Turkey", value: "Turkey" },
    { label: "Uganda", value: "Uganda" },
    { label: "Ukraine", value: "Ukraine" },
    { label: "United Arab Emirates", value: "United Arab Emirates" },
    { label: "United Kingdom", value: "United Kingdom" },
    { label: "United States", value: "United States" },
    { label: "Uruguay", value: "Uruguay" },
    { label: "Uzbekistan", value: "Uzbekistan" },
    { label: "Venezuela", value: "Venezuela" },
    { label: "Vietnam", value: "Vietnam" },
    { label: "Yemen", value: "Yemen" },
    { label: "Zambia", value: "Zambia" },
    { label: "Zimbabwe", value: "Zimbabwe" }
  ].sort((a, b) => a.label.localeCompare(b.label));
};

export const fetchPaperDiscountRules = async ({ conferenceApi, conferenceId }) => {
  const response = await fetch(
    `${conferenceApi}/${conferenceId}/master/type/paperDiscount/details`
  );
  if (!response.ok) {
    return [];
  }
  const details = await response.json();
  return Array.isArray(details) ? details : [];
};

export const createPaymentOrder = async ({ orderUrl, payload }) => {
  const response = await fetch(orderUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    order: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.order = await response.json();
  return result;
};

export const savePaymentWithBreakdown = async ({ paymentSaveUrl, payload }) => {
  const response = await fetch(paymentSaveUrl, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const confirmRegistration = async ({
  registrationBaseUrl,
  registrationId,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/registration/${registrationId}/confirm`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    confirmed: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.confirmed = await response.json();
  return result;
};

export const saveConferenceRegistration = async ({
  conferenceRegistrationUrl,
  registrationId,
  payload,
  forceCreate = false,
}) => {
  const isUpdate = !forceCreate && !!registrationId;
  const response = await fetch(
    isUpdate ? `${conferenceRegistrationUrl}/${registrationId}` : conferenceRegistrationUrl,
    {
      method: isUpdate ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const uploadRegistrationFile = async ({
  registrationBaseUrl,
  registrationId,
  fileType,
  payload,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/registration/${registrationId}/file/${fileType}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const savePaperSubmission = async ({
  registrationBaseUrl,
  paperId,
  payload,
}) => {
  const isUpdate = !!paperId;
  const response = await fetch(
    isUpdate
      ? `${registrationBaseUrl}/api/papers/${paperId}`
      : `${registrationBaseUrl}/api/papers`,
    {
      method: isUpdate ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const deleteCoAuthorsByPaperId = async ({
  registrationBaseUrl,
  paperId,
}) => {
  const response = await fetch(
    `${registrationBaseUrl}/api/coauthors/paper/${paperId}`,
    { method: "DELETE" }
  );

  return {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
  };
};

export const saveCoAuthor = async ({ registrationBaseUrl, payload }) => {
  const response = await fetch(`${registrationBaseUrl}/api/coauthors`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const result = {
    ok: response.ok,
    status: response.status,
    statusText: response.statusText,
    data: null,
    errorText: "",
  };

  if (!response.ok) {
    result.errorText = await response.text();
    return result;
  }

  result.data = await response.json();
  return result;
};

export const fetchRegistrationById = async ({
  conferenceRegistrationUrl,
  registrationId,
}) => {
  const response = await fetch(`${conferenceRegistrationUrl}/${registrationId}`);

  if (!response.ok) {
    return null;
  }

  return await response.json();
};
